<template>
  <div class="ap-field">
    <label>{{ label }} <span v-if="required" class="ap-req">*</span></label>
    <div class="ap-phone-wrap" :class="{ 'has-error': touched && localNumber && !isValid }" ref="phoneWrap">

      <!-- Country code button -->
      <button type="button" class="ap-dial-btn" @click.stop="toggleDropdown">
        <span class="fi ap-dial-flag" :class="'fi-' + selected.iso.toLowerCase()"></span>
        <span class="ap-dial-code">+{{ selected.dial }}</span>
        <fa-icon :icon="['fas', 'chevron-down']" class="ap-dial-chevron" :class="{ open: dropdownOpen }" />
      </button>

      <span class="ap-dial-sep"></span>

      <!-- Number input -->
      <input
        :value="localNumber"
        type="tel"
        :placeholder="placeholder"
        class="ap-phone-num"
        @input="onNumberInput"
        @blur="touched = true"
      />

      <!-- Dropdown — fixed position to escape any parent overflow clipping -->
      <div
        v-if="dropdownOpen"
        ref="dropdown"
        class="ap-dial-dropdown"
        :style="dropdownFixedStyle"
        @click.stop
      >
        <div class="ap-dial-search-wrap">
          <fa-icon :icon="['fas', 'search']" class="ap-dial-search-icon" />
          <input
            ref="searchInput"
            v-model="search"
            class="ap-dial-search"
            placeholder="Search country or code…"
          />
        </div>
        <div class="ap-dial-list" ref="dialList">
          <button
            v-for="c in filteredCountries"
            :key="c.iso"
            type="button"
            class="ap-dial-item"
            :class="{ active: c.iso === selected.iso }"
            @click="selectCountry(c)"
          >
            <span class="fi ap-dial-item-flag" :class="'fi-' + c.iso.toLowerCase()"></span>
            <span class="ap-dial-item-name">{{ c.name }}</span>
            <span class="ap-dial-item-dial">+{{ c.dial }}</span>
          </button>
          <div v-if="!filteredCountries.length" class="ap-dial-empty">No results</div>
        </div>
      </div>
    </div>

    <span v-if="touched && localNumber && !isValid" class="ap-err">
      <fa-icon :icon="['fas', 'exclamation-circle']" /> Please enter a valid phone number.
    </span>
    <span v-if="hint" class="ap-hint">{{ hint }}</span>
  </div>
</template>

<script>
import countriesMixin, { COUNTRIES, COUNTRIES_FOR_PARSE } from '@/mixins/countriesMixin'

export default {
  name: 'PhoneInput',
  mixins: [countriesMixin],
  props: {
    value: { type: String, default: '' },
    label: { type: String, default: 'Phone' },
    required: { type: Boolean, default: false },
    placeholder: { type: String, default: 'e.g. 123456789' },
    hint: { type: String, default: '' }
  },
  data () {
    return {
      selected: COUNTRIES[0], // Malaysia by default
      localNumber: '',
      touched: false,
      dropdownOpen: false,
      search: '',
      dropdownPos: { top: 0, left: 0, width: 290 }
    }
  },
  computed: {
    isValid () {
      const combined = this.selected.dial + this.localNumber
      return /^\d{7,15}$/.test(combined) && /^\d{4,12}$/.test(this.localNumber)
    },
    filteredCountries () {
      const q = this.search.trim().toLowerCase()
      if (!q) return this.allCountries
      return this.allCountries.filter(c =>
        c.name.toLowerCase().includes(q) ||
        c.dial.includes(q) ||
        c.iso.toLowerCase().includes(q)
      )
    },
    dropdownFixedStyle () {
      return {
        position: 'fixed',
        top: this.dropdownPos.top + 'px',
        left: this.dropdownPos.left + 'px',
        width: this.dropdownPos.width + 'px'
      }
    }
  },
  watch: {
    value: {
      immediate: true,
      handler (val) {
        if (val === this.selected.dial + this.localNumber) return
        this.parseValue(val)
      }
    },
    dropdownOpen (open) {
      if (open) {
        this.$nextTick(() => {
          if (this.$refs.searchInput) this.$refs.searchInput.focus()
          // Scroll active item into view inside the list
          const activeEl = this.$refs.dialList && this.$refs.dialList.querySelector('.ap-dial-item.active')
          if (activeEl) activeEl.scrollIntoView({ block: 'nearest' })
        })
      } else {
        this.search = ''
      }
    }
  },
  mounted () {
    document.addEventListener('click', this.onOutsideClick)
    window.addEventListener('scroll', this.closeDropdown, true)
    window.addEventListener('resize', this.closeDropdown)
  },
  beforeDestroy () {
    document.removeEventListener('click', this.onOutsideClick)
    window.removeEventListener('scroll', this.closeDropdown, true)
    window.removeEventListener('resize', this.closeDropdown)
  },
  methods: {
    parseValue (val) {
      const digits = (val || '').replace(/\D/g, '')
      if (!digits) { this.localNumber = ''; return }
      for (const c of COUNTRIES_FOR_PARSE) {
        if (digits.startsWith(c.dial)) {
          this.selected = c
          this.localNumber = digits.slice(c.dial.length)
          return
        }
      }
      this.localNumber = digits
    },
    onNumberInput (e) {
      this.touched = true
      this.localNumber = e.target.value.replace(/\D/g, '')
      this.$emit('input', this.selected.dial + this.localNumber)
    },
    selectCountry (c) {
      this.selected = c
      this.dropdownOpen = false
      this.$emit('input', this.selected.dial + this.localNumber)
    },
    toggleDropdown () {
      if (!this.dropdownOpen) {
        const rect = this.$refs.phoneWrap.getBoundingClientRect()
        this.dropdownPos = {
          top: rect.bottom + 6,
          left: rect.left,
          width: Math.max(290, rect.width)
        }
      }
      this.dropdownOpen = !this.dropdownOpen
    },
    closeDropdown (e) {
      // Allow scrolling inside the dropdown list — only close on external scroll
      if (e && this.$refs.dropdown && this.$refs.dropdown.contains(e.target)) return
      this.dropdownOpen = false
    },
    onOutsideClick (e) {
      if (this.$refs.phoneWrap && !this.$refs.phoneWrap.contains(e.target)) {
        this.dropdownOpen = false
      }
    }
  }
}
</script>
